/**
 * Field definitions for StructuresTab grids (structures and upload)
 * 
 * NOTE: This file is being gradually refactored. New grids should use:
 * - /shared/config/baseFieldDefinitions.js for common fields
 * - /shared/config/fieldUtils.jsx for utilities
 * - Feature-specific field configs (e.g., /features/inputs/config/)
 * 
 * This file still contains:
 * - Structures grid column definitions
 * - Upload grid column definitions
 * - Pattern extraction field mappings
 * - Metadata editing field configurations
 * 
 * Grid support:
 * - Use 'grids' array to specify which grids include this field
 * - Use 'gridOverrides' for grid-specific configurations (including visibility)
 */

import React from 'react';
import { Chip, Typography, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

/**
 * Shared render function for summary fields in zipfiles grid
 * Displays "varied (n)" chip when values differ, otherwise shows the single value
 */
const renderSummaryCell = (params, context) => {
  const theme = context?.theme;
  if (params.value?.type === 'varied') {
    return (
      <Chip
        label={`varied (${params.value.count})`}
        size="small"
        variant="outlined"
        sx={{ 
          height: theme?.dimensions?.chip?.tiny,
          fontSize: theme?.typography?.fontSize?.xs,
          borderColor: theme?.palette?.grey?.[400]
        }}
      />
    );
  }
  return (
    <Typography sx={{ fontSize: theme?.typography?.fontSize?.sm }}>
      {params.value}
    </Typography>
  );
};

/**
 * Master field definitions
 * Each field has complete configuration for all its uses
 * grids: Array of grid names where this field appears
 * gridOverrides: Object with grid-specific overrides
 */
export const FIELD_DEFINITIONS = [
  {
    id: 'id_',
    gridField: 'id_',
    patternField: null, // Not available for pattern extraction
    headerName: 'ID',
    width: (theme) => theme ? parseInt(theme.spacing(8)) : 64, // 8 * 8 = 64px (increased from 48px)
    type: 'string',  // Changed to string since spec rows use string IDs like '67-1'
    grids: ['structures', 'zipfiles', 'entries'],  // Which grids include this field
    inGrid: true,  // Legacy - kept for backward compatibility
    inPattern: false,
    editable: false,
    sortable: true,
    defaultVisible: true,
    searchable: true,  // Include in quick search
    inSuggestions: false,  // Don't include ID in suggestions
    valueGetter: (value, row) => row?.id_ || row?.id,
    searchValueGetter: (row) => row?.id_?.toString() || row?.id?.toString() || ''
  },
  {
    id: 'source_path',
    gridField: 'source_path',
    patternField: 'source_path',
    headerName: 'Source Path',
    width: (theme) => theme ? parseInt(theme.spacing(25)) : 200, // 25 * 8 = 200px minimum
    minWidth: 200, // Set minimum width
    flex: 1,
    type: 'text',
    inGrid: true,
    inPattern: true,
    editable: false,
    sortable: true,
    defaultVisible: true,
    searchable: true,  // Search by full path
    inSuggestions: false,  // Don't show in suggestions
    description: 'Source file path',
    valueGetter: (value, row) => row?.source_path || row?.filename || `prep_${row?.id_ || row?.id}`
  },
  {
    id: 'name',
    gridField: 'name',
    headerName: 'Name',
    flex: 2,
    minWidth: 200,
    type: 'text',
    grids: ['zipfiles'],
    sortable: true,
    defaultVisible: true,
    searchable: true
  },
  {
    id: 'entry_count',
    gridField: 'entry_count',
    headerName: 'Entries',
    width: (theme) => theme ? parseInt(theme.spacing(10)) : 80,
    align: 'center',
    headerAlign: 'center',
    type: 'number',
    grids: ['zipfiles'],
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row.entry_ids?.length || 0
  },
  {
    id: 'clusterspec',
    gridField: 'clusterspec',
    headerName: 'Cutoff',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'text',
    grids: ['zipfiles'],
    sortable: true,
    defaultVisible: true,
    needsSummary: true,
    entryPath: 'clusterspec.name',
    valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'clusterspec'),
    renderCell: renderSummaryCell
  },
  {
    id: 'pocketspec',
    gridField: 'pocketspec',
    headerName: 'Capping',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'text',
    grids: ['zipfiles'],
    sortable: true,
    defaultVisible: true,
    needsSummary: true,
    entryPath: 'pocketspec.name',
    valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'pocketspec'),
    renderCell: renderSummaryCell
  },
  {
    id: 'inputspec',
    gridField: 'inputspec', 
    headerName: 'Config',
    width: (theme) => theme ? parseInt(theme.spacing(11)) : 88,
    type: 'text',
    grids: ['zipfiles'],
    sortable: true,
    defaultVisible: true,
    needsSummary: true,
    entryPath: 'inputspec.name',
    valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'inputspec'),
    renderCell: renderSummaryCell
  },

  {
    id: 'project',
    gridField: 'project',
    patternField: 'project',
    apiField: 'project',
    headerName: 'Project',
    width: (theme) => theme ? parseInt(theme.spacing(24)) : 192, 
    type: 'text',
    grids: ['structures', 'zipfiles', 'entries', 'upload'],
    needsSummary: true,  // Flag for summary calculation in zipfiles
    entryPath: 'prep.project',  // Path to access in entry data
    gridOverrides: {
      zipfiles: {
        valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'project'),
        renderCell: renderSummaryCell
      }
    },
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,
    searchable: true,
    inSuggestions: true,
    description: 'Project name'
  },
  {
    id: 'target',
    gridField: 'target',
    patternField: 'target',
    apiField: 'target',
    headerName: 'Target',
    width: (theme) => theme ? parseInt(theme.spacing(8)) : 64, // 10 * 8 = 80px
    type: 'text',
    grids: ['structures', 'zipfiles', 'entries', 'upload'],
    needsSummary: true,
    entryPath: 'prep.target',
    gridOverrides: {
      zipfiles: {
        valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'target'),
        renderCell: renderSummaryCell
      }
    },
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,
    searchable: true,
    inSuggestions: true,
    description: 'Target protein/system'
  },
  {
    id: 'mutation',
    gridField: 'mutation',
    patternField: 'mutation',
    apiField: 'mutation',
    headerName: 'Mutation',
    width: (theme) => theme ? parseInt(theme.spacing(10)) : 80, // 10 * 8 = 80px
    type: 'text',
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: false, // Hidden by default
    searchable: true,
    inSuggestions: true,
    description: 'Mutation identifier'
  },
  {
    id: 'subgroup',
    gridField: 'subgroup',
    patternField: 'subgroup',
    apiField: 'subgroup',
    headerName: 'Subgroup',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96, // 14 * 8 = 112px (increased for header)
    type: 'text',
    grids: ['structures', 'zipfiles', 'upload'],
    needsSummary: true,
    entryPath: 'prep.subgroup',
    gridOverrides: {
      zipfiles: {
        valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'subgroup'),
        renderCell: renderSummaryCell
      }
    },
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,
    searchable: true,
    inSuggestions: true,
    description: 'Subgroup classification'
  },
  {
    id: 'origin_name',
    gridField: 'origin_name',
    patternField: 'origin',  // Note: pattern uses 'origin', grid uses 'origin_name'
    apiField: 'origin_name',
    headerName: 'Origin',
    width: (theme) => theme ? parseInt(theme.spacing(8)) : 64, // 10 * 8 = 80px
    type: 'text',
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,
    searchable: true,
    inSuggestions: true,
    description: 'Origin name/identifier',
    valueGetter: (value, row) => {
      const originName = row?.origin_name || '';
      const originType = row?.origin_type || '';
      return originType === 'rcsb' ? `${originName} (PDB)` : originName;
    }
  },
  {
    id: 'prep_workflow',
    gridField: 'prep_workflow',
    patternField: 'workflow',  // Note: pattern uses 'workflow', grid uses 'prep_workflow'
    apiField: 'prep_workflow',
    headerName: 'Workflow',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96, // 12 * 8 = 96px
    type: 'text',
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,
    searchable: true,
    inSuggestions: true,
    description: 'Preparation workflow'
  },
  {
    id: 'batch',
    gridField: 'batch',
    patternField: 'batch',
    apiField: 'batch',
    headerName: 'Batch',
    width: (theme) => theme ? parseInt(theme.spacing(16)) : 144, 
    type: 'text',
    grids: ['structures', 'zipfiles', 'upload'],
    needsSummary: true,
    entryPath: 'prep.batch',
    gridOverrides: {
      zipfiles: {
        valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'batch'),
        renderCell: renderSummaryCell
      }
    },
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,
    searchable: true,
    inSuggestions: true,
    description: 'Batch identifier'
  },
  {
    id: 'ligand_name',
    gridField: 'ligand_name',
    patternField: 'ligand',  // Note: pattern uses 'ligand', grid uses 'ligand_name'
    apiField: 'ligand_name',
    headerName: 'Ligand',
    width: (theme) => theme ? parseInt(theme.spacing(16)) : 96, // 16 * 8 = 128px
    type: 'text',
    grids: ['structures', 'zipfiles', 'entries', 'upload'],
    needsSummary: true,
    entryPath: 'prep.ligand_name',
    gridOverrides: {
      zipfiles: { 
        gridField: 'ligandName',
        valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'ligandName'),
        renderCell: renderSummaryCell,
        defaultVisible: false  // Hidden only in zipfiles
      },
      entries: { 
        gridField: 'ligandName',
        valueGetter: (value, row) => row.prep?.ligand_name || '-',
        defaultVisible: false  // Hidden only in entries
      }
    },
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,  // Visible by default in structures and upload
    searchable: true,
    inSuggestions: true,
    description: 'Ligand/compound name'
  },
  {
    id: 'ligand_tag',
    gridField: 'ligand_tag',
    patternField: 'ligand_tag',
    apiField: 'ligand_tag',
    headerName: 'Ligand Tag',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120, // 15 * 8 = 120px (increased for header)
    type: 'text',
    grids: ['structures', 'zipfiles', 'entries', 'upload'],
    needsSummary: true,
    entryPath: 'prep.ligand_tag',
    gridOverrides: {
      zipfiles: { 
        gridField: 'ligandTag',
        valueGetter: (value, row, context) => context?.getSummaryValue?.(row, 'ligandTag'),
        renderCell: renderSummaryCell,
        defaultVisible: false  // Hidden only in zipfiles
      },
      entries: { 
        gridField: 'ligandTag',
        valueGetter: (value, row) => row.prep?.ligand_tag || '-',
        defaultVisible: false  // Hidden only in entries
      }
    },
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,  // Visible by default in structures and upload
    searchable: true,
    inSuggestions: true,
    description: 'Ligand tag/identifier'
  },
  {
    id: 'author',
    gridField: 'author',
    patternField: 'author',
    apiField: 'author',
    headerName: 'Author',
    width: (theme) => theme ? parseInt(theme.spacing(18)) : 144, 
    type: 'text',
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: true,
    defaultVisible: true,
    searchable: true,
    inSuggestions: true,
    description: 'Author/creator',
    needsSummary: true,  // Enable summary calculation for zipfiles
    entryPath: 'prep.author'  // Path to access author in entry data
  },
  {
    id: 'notes',
    gridField: 'notes',
    patternField: 'notes',
    apiField: 'notes',
    headerName: 'Notes',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120, // 15 * 8 = 120px
    type: 'text',
    inGrid: true,
    inPattern: true,
    editable: true,
    sortable: false,
    defaultVisible: false, // Hidden by default
    searchable: true,  // Searchable but not in suggestions
    inSuggestions: false,  // Don't show note content in suggestions
    description: 'Notes/description'
  },
  {
    id: 'prepared_at',
    gridField: 'prepared_at',
    patternField: 'prepared',  // Note: pattern uses 'prepared', grid uses 'prepared_at'
    apiField: 'prepared_at',
    headerName: 'Prepared',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'dateTime',
    inGrid: true,
    inPattern: true,
    editable: false,
    sortable: true,
    defaultVisible: true,
    searchable: true,  // Enable searching on this field
    inSuggestions: true,  // Include in autocomplete suggestions
    description: 'Preparation date (YYYY-MM-DD)',
    valueGetter: (value, row) => {
      const dateStr = row?.prepared_at;
      return dateStr ? new Date(dateStr) : null;
    },
    renderCell: (params) => {
      const date = params.value;
      if (!date) return '-';
      const yy = date.getFullYear().toString().slice(-2);
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');
      return `${yy}/${mm}/${dd}`;
    },
    // Format date for search - supports multiple formats
    searchValueGetter: (row) => {
      const dateStr = row?.prepared_at;
      if (!dateStr) return '';

      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';

      const yy = date.getFullYear().toString().slice(-2);
      const yyyy = date.getFullYear().toString();
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');

      // Return multiple searchable formats separated by spaces
      // This allows matching any of these formats
      return `${yy}/${mm}/${dd} ${yy}${mm}${dd} ${yyyy}-${mm}-${dd} ${mm}/${dd}/${yy}`;
    },
    // Format for autocomplete suggestions (displayed format)
    suggestionValueGetter: (row) => {
      const dateStr = row?.prepared_at;
      if (!dateStr) return '';

      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';

      const yy = date.getFullYear().toString().slice(-2);
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');

      return `${yy}/${mm}/${dd}`;
    }
  },
  {
    id: 'tags',
    gridField: 'tags',
    patternField: null,  // Tags not in pattern extraction currently
    apiField: 'tags',
    headerName: 'Tags',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120, // 15 * 8 = 120px
    type: 'text',
    grids: ['structures'],
    inGrid: true,
    inPattern: false,
    editable: true,
    sortable: false,
    defaultVisible: false, // Hidden by default
    searchable: true,  // Searchable to find by tag
    inSuggestions: false,  // Don't show individual tags in suggestions
    description: 'Tags for categorization',
    valueGetter: (value, row) => {
      const tags = row?.tags || [];
      return Array.isArray(tags) ? tags.join(' ') : String(tags);
    }
  },
  {
    id: 'owner',
    gridField: 'owner',
    headerName: 'Owner',
    width: (theme) => theme ? parseInt(theme.spacing(10)) : 80,
    type: 'text',
    grids: ['zipfiles'],
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row, context) => {
      if (!row.owner) return '';
      const fullname = context?.getUserFullname?.(row.owner.id_);
      return fullname || row.owner.username || '';
    },
    renderCell: (params, context) => {
      const theme = context?.theme;
      return (
        <Typography sx={{ fontSize: theme?.typography?.fontSize?.sm }}>
          {params.value}
        </Typography>
      );
    }
  },
  {
    id: 'created_at_zip',
    gridField: 'created_at',
    headerName: 'Created',
    width: (theme) => theme ? parseInt(theme.spacing(10)) : 80,
    type: 'text',  // Changed from 'date' to 'text' since we're returning a formatted string
    grids: ['zipfiles'],
    sortable: true,
    defaultVisible: false,  // Hidden by default to make space for spec columns
    valueGetter: (value, row) => {
      if (!row.created_at) return '';
      const date = new Date(row.created_at);
      const yy = date.getFullYear().toString().slice(-2);
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');
      return `${yy}/${mm}/${dd}`;
    }
  },
  {
    id: 'download',
    gridField: 'download',
    headerName: 'Download',
    width: (theme) => theme ? parseInt(theme.spacing(11)) : 88,
    align: 'center',
    headerAlign: 'center',
    type: 'action',
    grids: ['zipfiles'],
    sortable: false,
    defaultVisible: true,
    renderCell: (params, context) => {
      const theme = context?.theme;
      const handleDownload = context?.handleDownload;
      // Don't show download button for spec rows
      if (params.row.isSpecRow || params.row.noDownload) {
        return null;
      }
      return (
        <IconButton
          size="small"
          onClick={(e) => handleDownload?.(params.row, e)}
          sx={{ 
            padding: theme?.spacing(0.5),
            '&:hover': {
              backgroundColor: theme?.palette?.action?.hover
            }
          }}
        >
          <DownloadIcon sx={{ fontSize: theme?.dimensions?.icon?.small }} />
        </IconButton>
      );
    }
  },
  {
    id: 'clusterName',
    gridField: 'clusterName',
    headerName: 'Cluster',
    width: (theme) => theme ? parseInt(theme.spacing(18)) : 150,
    type: 'text',
    grids: ['entries'],
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row.cluster?.name || '-'
  },
  {
    id: 'inputSpec',
    gridField: 'inputSpec',
    headerName: 'Input Spec',
    flex: 1,
    minWidth: 150,
    type: 'text',
    grids: ['entries'],
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row.inputspec?.name || '-'
  },
  {
    id: 'view',
    gridField: 'view',
    headerName: 'View',
    width: (theme) => theme ? parseInt(theme.spacing(9)) : 70,
    align: 'center',
    headerAlign: 'center',
    type: 'action',
    grids: ['entries'],
    sortable: false,
    defaultVisible: true,
    renderCell: (params, context) => {
      const theme = context?.theme;
      const onEntrySelect = context?.onEntrySelect;
      const selectedEntry = context?.selectedEntry;
      return (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (onEntrySelect) onEntrySelect(params.row);
          }}
          sx={{ 
            padding: theme?.spacing(0.5),
            color: params.row.id_ === selectedEntry?.id_ 
              ? theme?.palette?.primary?.main 
              : theme?.palette?.text?.secondary
          }}
        >
          <VisibilityIcon sx={{ fontSize: theme?.dimensions?.icon?.small }} />
        </IconButton>
      );
    }
  }
];

/**
 * Get grid columns from field definitions for a specific grid
 * @param {string} gridName - Name of the grid ('structures', 'zipfiles', 'entries', 'upload')
 * @param {Object} options - Configuration options
 * @returns {Array} Column definitions for DataGrid
 */
export const getGridColumns = (gridName, options = {}) => {
  const { theme = null, isPreviewMode = false, previewData = [], context = {} } = options;
  
  return FIELD_DEFINITIONS
    .filter(field => {
      // Check if field is included in this grid
      if (field.grids && Array.isArray(field.grids)) {
        return field.grids.includes(gridName);
      }
      // Backward compatibility with inGrid flag for structures
      if (gridName === 'structures' && field.inGrid) {
        return true;
      }
      return false;
    })
    .map(field => {
      // Start with base field properties
      let gridField = field.gridField;
      
      // Apply grid-specific field name override if exists
      if (field.gridOverrides?.[gridName]?.gridField) {
        gridField = field.gridOverrides[gridName].gridField;
      }
      
      const column = {
        field: gridField,
        headerName: field.headerName,
        width: typeof field.width === 'function' ? field.width(theme) : field.width,
        minWidth: field.minWidth,
        flex: field.flex,
        type: field.type,
        sortable: field.sortable !== false,
        align: field.align,
        headerAlign: field.headerAlign
      };
      
      // Apply grid-specific overrides
      if (field.gridOverrides?.[gridName]) {
        const overrides = field.gridOverrides[gridName];
        Object.keys(overrides).forEach(key => {
          if (key !== 'gridField') { // gridField already handled above
            column[key] = overrides[key];
          }
        });
      }
      
      // Skip complex valueGetters for summary fields in zipfiles grid
      // Since values are pre-calculated in tree data
      if (gridName === 'zipfiles' && field.needsSummary) {
        // Remove valueGetter and renderCell for summary fields
        delete column.valueGetter;
        delete column.renderCell;
      } else {
        // Add value getter for non-summary fields
        if (column.valueGetter) {
          // Grid-specific valueGetter from overrides
          const originalGetter = column.valueGetter;
          column.valueGetter = (value, row, column, apiRef) => {
            // Handle both signatures: (value, row) for regular DataGrid
            // and potential params object for special cases
            const actualRow = row || value?.row || value;
            const actualValue = actualRow?.[gridField];
            return originalGetter(actualValue, actualRow, context);
          };
        } else if (field.valueGetter) {
          // Default field valueGetter
          column.valueGetter = (value, row, column, apiRef) => {
            const actualRow = row || value?.row || value;
            const actualValue = actualRow?.[gridField];
            return field.valueGetter(actualValue, actualRow, { isPreviewMode, previewData, ...context });
          };
        }
      }
      
      if (field.editable && gridName === 'structures') {
        // Add preview support for editable fields in structures grid
        column.valueGetter = (value, row) => {
          const actualRow = row || value?.row || value;
          if (isPreviewMode) {
            const preview = previewData.find(p => p.id === actualRow?.id);
            if (preview?.[gridField] !== undefined) return preview[gridField];
          }
          return actualRow?.[gridField] || '';
        };
      }
      
      // Add render cell if defined (skip for summary fields in zipfiles)
      if (!(gridName === 'zipfiles' && field.needsSummary)) {
        if (column.renderCell) {
          // Grid-specific renderCell from overrides
          const originalRenderer = column.renderCell;
          column.renderCell = (params) => originalRenderer(params, context);
        } else if (field.renderCell) {
          // Field-level renderCell also needs context wrapper
          const originalRenderer = field.renderCell;
          column.renderCell = (params) => originalRenderer(params, context);
        }
      }
      
      return column;
    });
};

/**
 * Legacy function for backward compatibility
 */
export const getStructureGridColumns = (options) => {
  return getGridColumns('structures', options);
};

/**
 * Get pattern extraction fields (for backward compatibility with existing code)
 */
export const getAvailableFields = () => {
  return FIELD_DEFINITIONS
    .filter(field => field.inPattern && field.patternField)
    .map(field => ({
      name: field.patternField,
      apiField: field.apiField || field.gridField,
      description: field.description
    }));
};

/**
 * Get default column visibility for a specific grid
 */
export const getDefaultColumnVisibility = (gridName) => {
  return FIELD_DEFINITIONS
    .filter(field => {
      if (field.grids && field.grids.includes(gridName)) {
        // Check grid-specific override first
        if (field.gridOverrides?.[gridName]?.defaultVisible !== undefined) {
          return field.gridOverrides[gridName].defaultVisible === false;
        }
        // Fall back to field's default
        return field.defaultVisible === false;
      }
      // Backward compatibility for structures grid
      if (gridName === 'structures' && field.inGrid) {
        return field.defaultVisible === false;
      }
      return false;
    })
    .reduce((acc, field) => {
      // Get the correct field name for this grid
      let gridField = field.gridField;
      if (field.gridOverrides?.[gridName]?.gridField) {
        gridField = field.gridOverrides[gridName].gridField;
      }
      acc[gridField] = false;
      return acc;
    }, {});
};

/**
 * Get fields that need summary calculation for zipfiles grid
 * Returns array of field definitions with their field names and entry paths
 */
export const getZipfileSummaryFields = () => {
  return FIELD_DEFINITIONS
    .filter(field => field.needsSummary === true)
    .map(field => ({
      fieldName: field.gridOverrides?.zipfiles?.gridField || field.gridField,
      entryPath: field.entryPath,
      id: field.id
    }));
};

/**
 * Legacy constant for backward compatibility
 */
export const DEFAULT_COLUMN_VISIBILITY = getDefaultColumnVisibility('structures');
